import {
  purchaseEventApi,
  PendingPurchaseEvent,
} from 'src/lib/services/api/purchase-event-api/purchase-event-client';
import { GoogleTagManager } from 'src/services/GoogleTagManager';
import { customPosthogEvent } from 'src/providers/posthog';
import { Logger } from 'src/utils/logger';

declare global {
  interface Window {
    posthog?: {
      capture: (event: string, properties?: Record<string, any>) => void;
    };
  }
}

interface AnalyticsPlatformError {
  platform: 'GTM' | 'Meta Pixel' | 'PostHog';
  message: string;
  error?: any;
}

interface EventProcessingResult {
  eventId: number;
  success: boolean;
  errors: AnalyticsPlatformError[];
}

export class PurchaseEventService {
  /**
   * Fetch and process all pending purchase events
   * Only marks events as processed if ALL analytics platforms succeed
   */
  static async processPendingEvents(): Promise<void> {
    try {
      Logger.info('Claiming and fetching pending purchase events', 'PurchaseEventService');

      const { events } = await purchaseEventApi.claimAndGetPendingEvents();

      if (events.length === 0) {
        Logger.info('No pending purchase events', 'PurchaseEventService');
        return;
      }

      Logger.info(
        `Processing ${events.length} pending purchase events`,
        'PurchaseEventService',
        {
          count: events.length,
        },
      );

      const results: EventProcessingResult[] = [];

      for (const event of events) {
        const result = await this.sendPurchaseEvent(event);
        results.push(result);

        if (result.success) {
          Logger.info(
            'Successfully sent purchase event to all platforms',
            'PurchaseEventService',
            {
              eventId: event.id,
              eventType: event.event_type,
            },
          );
        } else {
          Logger.error(
            'Failed to send purchase event to some platforms',
            'PurchaseEventService',
            {
              eventId: event.id,
              failedPlatforms: result.errors.map((e) => e.platform),
              errors: result.errors,
            },
          );
        }
      }

      // Only mark events that were fully successful
      const successfulEventIds = results.filter((r) => r.success).map((r) => r.eventId);

      if (successfulEventIds.length > 0) {
        try {
          await purchaseEventApi.markProcessed(successfulEventIds);
          Logger.info('Marked successfully processed events', 'PurchaseEventService', {
            count: successfulEventIds.length,
            total: results.length,
          });
        } catch (error) {
          Logger.error('Failed to mark events as processed', 'PurchaseEventService', {
            successfulEventIds,
            error,
          });
          // Events were sent to analytics but marking failed
          // User will see duplicates on next login - log for manual cleanup
        }
      }

      const failedCount = results.length - successfulEventIds.length;
      if (failedCount > 0) {
        // Log platform-specific failure statistics
        const platformFailures = this.aggregatePlatformFailures(results);

        Logger.warn(
          'Some events failed and will be retried on next login',
          'PurchaseEventService',
          {
            failedCount,
            platformFailures,
          },
        );
      }
    } catch (error) {
      Logger.error('Error processing pending purchase events', 'PurchaseEventService', error);
    }
  }

  /**
   * Aggregate platform-specific failures for diagnostics
   */
  private static aggregatePlatformFailures(
    results: EventProcessingResult[],
  ): Record<string, number> {
    const failures: Record<string, number> = {};

    results
      .filter((r) => !r.success)
      .forEach((r) => {
        r.errors.forEach((error) => {
          failures[error.platform] = (failures[error.platform] || 0) + 1;
        });
      });

    return failures;
  }

  /**
   * Send purchase event to all analytics platforms
   * Returns success only if ALL platforms succeed
   */
  private static async sendPurchaseEvent(
    event: PendingPurchaseEvent,
  ): Promise<EventProcessingResult> {
    const { event_data } = event;
    const errors: AnalyticsPlatformError[] = [];

    // 1. Google Tag Manager
    try {
      if (typeof window.dataLayer === 'undefined') {
        // Ad blocker or script not loaded - acceptable, don't block processing
        Logger.warn('GTM not available, skipping', 'PurchaseEventService', {
          eventId: event.id,
        });
      } else {
        GoogleTagManager.event('purchase', {
          ecommerce: event_data,
        });
      }
    } catch (error) {
      // Actual error (script loaded but failed) - should retry
      const errorObj: AnalyticsPlatformError = {
        platform: 'GTM',
        message: error instanceof Error ? error.message : 'Unknown error',
        error,
      };
      errors.push(errorObj);
      Logger.error('GTM purchase event failed', 'PurchaseEventService', {
        eventId: event.id,
        platform: 'GTM',
        error: errorObj,
      });
    }

    // 2. Meta Pixel
    try {
      if (typeof window.fbq === 'undefined') {
        // Ad blocker or script not loaded - acceptable, don't retry
        Logger.warn('Meta Pixel not available, skipping', 'PurchaseEventService', {
          eventId: event.id,
        });
      } else {
        window.fbq('track', 'Purchase', {
          value: event_data.value,
          currency: event_data.currency,
        });
      }
    } catch (error) {
      // Actual error - should retry
      const errorObj: AnalyticsPlatformError = {
        platform: 'Meta Pixel',
        message: error instanceof Error ? error.message : 'Unknown error',
        error,
      };
      errors.push(errorObj);
      Logger.error('Meta Pixel purchase event failed', 'PurchaseEventService', {
        eventId: event.id,
        platform: 'Meta Pixel',
        error: errorObj,
      });
    }

    // 3. PostHog
    // Note: Can't use useDecoratedPostHog hook here as this is a static service class, not a React component
    // Using window.posthog directly with proper null checks (similar to hook's optional chaining pattern)
    try {
      if (typeof window.posthog === 'undefined' || !window.posthog) {
        // Ad blocker or script not loaded - acceptable, don't block processing
        Logger.warn('PostHog not available, skipping', 'PurchaseEventService', {
          eventId: event.id,
        });
      } else {
        window.posthog.capture(customPosthogEvent.recurringPaymentSuccess, {
          transaction_id: event_data.transaction_id,
          value: event_data.value,
          billing_interval: event_data.billing_interval,
        });
      }
    } catch (error) {
      // Actual error (script loaded but failed) - should retry
      const errorObj: AnalyticsPlatformError = {
        platform: 'PostHog',
        message: error instanceof Error ? error.message : 'Unknown error',
        error,
      };
      errors.push(errorObj);
      Logger.error('PostHog purchase event failed', 'PurchaseEventService', {
        eventId: event.id,
        platform: 'PostHog',
        error: errorObj,
      });
    }

    return {
      eventId: event.id,
      success: errors.length === 0,
      errors,
    };
  }
}
